import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import DemoPlayer from '@/pages/detail/components/DemoPlayer';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  formatDate,
  formatDuration,
  formatViews,
  getMovieById,
  getRelatedMovies,
} from '@/lib/movies';

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta({
    title: movie ? `${movie.title} - av免费网站` : '影片不存在 - av免费网站',
    description: movie
      ? `${movie.synopsis.slice(0, 70)}…（${movie.title}，${movie.year} 年 ${movie.region}，${movie.rating.toFixed(1)} 分，演示内容）`
      : '该影片不存在或已下架，请返回首页浏览其他影片。',
    keywords: movie
      ? `${movie.title},${movie.tags.join(',')},高清AV,AV在线观看,av免费网站`
      : 'av免费网站',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="max-w-page mx-auto px-4 md:px-8 py-24 md:py-32 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-500 flex items-center justify-center">
          <i className="ri-film-line text-3xl" />
        </span>
        <h1 className="mt-6 text-2xl md:text-3xl font-bold">影片不存在或已下架</h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-md mx-auto leading-relaxed">
          你访问的影片可能已经调整或链接有误。可以回到首页看看最近更新的内容，或者用搜索找找相似的影片。
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="h-11 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium flex items-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
          <Link
            to="/category/latest"
            className="h-11 px-6 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            浏览最新影片
          </Link>
          <Link
            to="/search"
            className="h-11 px-6 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索影片
          </Link>
        </div>
      </div>
    );
  }

  const saved = isFavorite(movie.id);
  const categoryKey = movie.categories.includes('classic')
    ? 'classic'
    : movie.categories.includes('hot')
      ? 'hot'
      : 'latest';
  const categoryLabel = categoryKey === 'classic' ? '经典影片' : categoryKey === 'hot' ? '热门影片' : '最新影片';
  const related = getRelatedMovies(movie, 5);

  const handleFavorite = () => {
    const next = toggleFavorite(movie.id);
    setToast(next ? '已加入收藏夹' : '已从收藏夹移除');
  };

  return (
    <div>
      <div className="relative overflow-hidden border-b border-background-200/70">
        <img
          src={movie.banner ?? movie.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-25 blur-md scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/90 to-background-50" />

        <div className="relative max-w-page mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <Link to={`/category/${categoryKey}`} className="hover:text-primary-400 transition-colors cursor-pointer">
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-800 line-clamp-1">{movie.title}</span>
          </nav>

          <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">
                {movie.latin}
              </p>
              <h1 className="mt-2 text-2xl md:text-4xl font-black tracking-tight text-foreground-950">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs md:text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5 text-primary-400">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-sm" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="w-1 h-1 rounded-full bg-background-400" />
                <span>{movie.year}</span>
                <span className="w-1 h-1 rounded-full bg-background-400" />
                <span>{movie.region}</span>
                <span className="w-1 h-1 rounded-full bg-background-400" />
                <span>{formatDuration(movie.duration)}</span>
                <span className="w-1 h-1 rounded-full bg-background-400" />
                <span>更新于 {formatDate(movie.updatedAt)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  saved
                    ? 'bg-accent-500/15 border border-accent-400/60 text-accent-300'
                    : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
                }`}
              >
                <i className={`${saved ? 'ri-heart-fill' : 'ri-heart-line'} text-lg w-5 h-5 flex items-center justify-center`} />
                {saved ? '已收藏' : '加入收藏'}
              </button>
              <Link
                to={`/category/${categoryKey}`}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-background-300/70 text-sm text-foreground-800 hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                返回分类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-page mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
          <div className="min-w-0">
            <DemoPlayer movie={movie} />

            <section className="mt-6 rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-6">
              <h2 className="text-base md:text-lg font-bold tracking-tight">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{movie.synopsis}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="h-8 px-3 rounded-full bg-secondary-100 text-secondary-900 text-xs flex items-center hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-5 rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-6">
              <h2 className="text-base md:text-lg font-bold tracking-tight">演职人员</h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs text-foreground-500">导演</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{movie.director}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">主演</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{movie.actors.join('、')}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">类型</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{movie.tags.join(' / ')}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">上映年份</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{movie.year} 年 · {movie.region}</dd>
                </div>
              </dl>
              <p className="mt-5 text-[11px] leading-relaxed text-foreground-500">
                以上名称、简介与演职人员信息均为虚构示例，仅用于产品界面演示，与任何真实人物或作品无关。
              </p>
            </section>
          </div>

          <aside className="min-w-0">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="rounded-lg overflow-hidden border border-background-300/60 bg-background-100">
                <div className="relative w-full aspect-video bg-background-200">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 影片封面`}
                    title={`${movie.title} - 影片海报`}
                    className="w-full h-full object-cover object-top"
                  />
                  <span className="absolute top-2.5 left-2.5 h-6 px-2 rounded-md bg-background-50/80 backdrop-blur-sm text-[10px] tracking-wider text-primary-400 font-label flex items-center">
                    HD · {movie.rating.toFixed(1)}
                  </span>
                </div>
                <dl className="divide-y divide-background-200/80 px-4">
                  <div className="flex items-center justify-between py-2.5">
                    <dt className="text-xs text-foreground-500">年份</dt>
                    <dd className="text-xs text-foreground-900">{movie.year}</dd>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <dt className="text-xs text-foreground-500">地区</dt>
                    <dd className="text-xs text-foreground-900">{movie.region}</dd>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <dt className="text-xs text-foreground-500">时长</dt>
                    <dd className="text-xs text-foreground-900">{formatDuration(movie.duration)}</dd>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <dt className="text-xs text-foreground-500">浏览</dt>
                    <dd className="text-xs text-foreground-900">{formatViews(movie.views)} 次</dd>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <dt className="text-xs text-foreground-500">更新时间</dt>
                    <dd className="text-xs text-foreground-900">{formatDate(movie.updatedAt)}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-background-300/60 bg-secondary-50 p-4">
                <p className="flex items-center gap-2 text-xs text-secondary-800">
                  <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center" />
                  收藏状态保存在当前浏览器
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-600">
                  清除浏览器数据或更换设备后，收藏记录不会同步。本站不收集任何个人观看记录。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12 md:mt-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">You May Also Like</p>
              <h2 className="mt-2 text-xl md:text-2xl font-bold tracking-tight">相关推荐</h2>
            </div>
            <Link
              to={`/category/${categoryKey}`}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              更多{categoryLabel}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} showSynopsis={false} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] animate-fade-up">
          <div className="flex items-center gap-2 h-11 px-4 rounded-md bg-background-800 text-background-50 text-sm shadow-none">
            <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}